import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, Box, CircularProgress, Typography, Alert } from '@mui/material';
import api from '../../utils/api';

const MIN_CHARS = 2;

/**
 * @param {'client'|'vendor'} scope
 * @param {{ id: string, company_name: string, account_type?: string }|null} valueAccount
 * @param {{ id: string, first_name: string, last_name: string, email?: string, label?: string }|null} valueContact
 * @param {(a: any, c: any) => void} onChange — fired when account or contact selection changes
 */
export default function CallAccountContactPicker({ scope, valueAccount, valueContact, onChange, disabled = false }) {
  const [accountInput, setAccountInput] = useState('');
  const [accountOptions, setAccountOptions] = useState([]);
  const [loadingAccts, setLoadingAccts] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const searchTimer = useRef(null);

  const label = scope === 'client' ? 'Client name (company)' : 'Vendor name (company)';

  const fetchAccounts = useCallback(
    async (q) => {
      const t = String(q).trim();
      if (t.length < MIN_CHARS) {
        setAccountOptions([]);
        return;
      }
      setLoadingAccts(true);
      try {
        const r = await api.get('/activities/lookup/accounts', {
          params: { scope, q: t },
        });
        setAccountOptions(r.data?.data || []);
      } catch {
        setAccountOptions([]);
      } finally {
        setLoadingAccts(false);
      }
    },
    [scope]
  );

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const scheduleSearch = (q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchAccounts(q), 300);
  };

  const loadContacts = useCallback(async (accountId) => {
    if (!accountId) {
      setContactOptions([]);
      return;
    }
    setLoadingContacts(true);
    try {
      const r = await api.get(`/activities/lookup/account-contacts/${accountId}`);
      setContactOptions(r.data?.data || []);
    } catch {
      setContactOptions([]);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const handleAccountChange = (_, v) => {
    onChange(v, null);
    if (!v?.id) {
      setContactOptions([]);
    }
  };

  const handleContactChange = (_, v) => {
    onChange(valueAccount, v);
  };

  // Keep input in sync when account selected from list
  useEffect(() => {
    if (valueAccount?.company_name) {
      setAccountInput(valueAccount.company_name);
    } else {
      setAccountInput('');
    }
  }, [valueAccount?.id, valueAccount?.company_name]);

  useEffect(() => {
    if (valueAccount?.id) {
      loadContacts(valueAccount.id);
    } else {
      setContactOptions([]);
    }
  }, [valueAccount?.id, loadContacts]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Type at least {MIN_CHARS} characters to search accounts. Only accounts marked{' '}
        {scope === 'client' ? '“client” (or “both”)' : '“vendor” (or “both”)'} in Accounts are shown.
      </Typography>
      <Autocomplete
        disabled={disabled}
        options={accountOptions}
        value={valueAccount}
        onChange={handleAccountChange}
        inputValue={accountInput}
        onInputChange={(_, v, reason) => {
          setAccountInput(v);
          if (reason === 'input' || reason === 'clear') {
            scheduleSearch(v);
          }
        }}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        getOptionLabel={(o) => (o && o.company_name ? o.company_name : '')}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <span>{o.company_name}</span>
            {o.account_code ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({o.account_code})
              </Typography>
            ) : null}
          </li>
        )}
        loading={loadingAccts}
        noOptionsText={
          accountInput.trim().length < MIN_CHARS ? `Type at least ${MIN_CHARS} characters` : 'No matching accounts'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Start typing company name or code…"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingAccts ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Autocomplete
        disabled={disabled || !valueAccount}
        options={contactOptions}
        value={valueContact}
        onChange={handleContactChange}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        getOptionLabel={(c) => (c ? c.label || [c.first_name, c.last_name].filter(Boolean).join(' ').trim() : '')}
        renderOption={(props, c) => (
          <li {...props} key={c.id}>
            <Box>
              <span>{[c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email}</span>
              {c.email ? (
                <Typography display="block" variant="caption" color="text.secondary">
                  {c.email}
                  {c.phone ? ` · ${c.phone}` : ''}
                </Typography>
              ) : null}
            </Box>
          </li>
        )}
        loading={loadingContacts}
        noOptionsText={
          valueAccount
            ? loadingContacts
              ? 'Loading…'
              : 'No contacts linked to this account — add one under Accounts'
            : 'Select an account first'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Who did you call?"
            placeholder="Choose a contact at this company…"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingContacts ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {valueAccount && !loadingContacts && contactOptions.length === 0 && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          This account has no linked contacts. Link people under <strong>Accounts</strong> before logging this call.
        </Alert>
      )}
    </Box>
  );
}
